import { useState, useEffect, useRef } from "react";
import {
  Upload, Trash2, Plus, RefreshCw, Save, Edit, Edit3, X, FileText,
  AlertCircle, CheckCircle2, Home, Image, Camera, ExternalLink,
  Clock, Download, Eye, Receipt, Wallet, Euro, Loader2, Lock,
  Building2, MapPin, Users, Phone, Mail, Calendar, Shield,
  User, History, ArrowLeft, ArrowRight, Wrench, MessageSquare,
  ChevronDown, ChevronUp, Ban, Send, Copy, Smartphone, Key, QrCode,
  ShieldCheck, ShieldAlert, ShieldX, Thermometer
} from "lucide-react";
import { Badge } from "../../../components/ui/badge";
import { Button } from "../../../components/ui/button";
import { Textarea } from "../../../components/ui/textarea";
import { API, getAuthHeaders, formatMontant, formatDate, openAuthedPdf } from "./helpers";
import { toast } from "sonner";

// Partition des documents générés par zone (Refonte Documents — LOT 1).
// Source unique = /devis-contrats. AMO = bibliothèque métier (Zone 2) ; tout le
// reste (mandat ANAH, avance, plan financement, procuration) = pièces officielles
// ANAH (Zone 3). L'union AMO ∪ ANAH couvre 100 % des types renvoyés par l'endpoint
// → aucun document ne disparaît (règle LOT 1 : zéro régression).
const AMO_TYPES = ["devis_amo", "contrat_amo", "facture_amo"];

// scope : "all" (défaut, rétrocompatible) | "amo" (Zone 2) | "anah" (Zone 3)
export function GeneratedDocsTab({ dossierId, tenantId, scope = "all" }) {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const load = async () => {
      try {
        const token = localStorage.getItem("session_token");
        const headers = { "X-Tenant-ID": tenantId || "tef", ...(token && { Authorization: `Bearer ${token}` }) };
        const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/devis-contrats`, { headers, credentials: "include" });
        if (res.ok) {
          const data = await res.json();
          setDocs(data.documents || []);
        }
      } catch (e) { console.error(e); }
      finally { setLoading(false); }
    };
    if (dossierId) load();
  }, [dossierId, tenantId]);

  const TYPE_TO_SLUG = {
    devis_amo: "devis-amo",
    contrat_amo: "contrat-amo",
    mandat_amo: "mandat-amo",
    demande_avance_anah: "demande-avance-anah",
    facture_amo: "facture-amo",
    plan_financement: "plan-financement",
    procuration_seing_prive: "procuration-seing-prive",
  };

  const handleDownload = async (type) => {
    const token = localStorage.getItem("session_token");
    const headers = { "X-Tenant-ID": tenantId || "tef", ...(token && { Authorization: `Bearer ${token}` }) };
    const slug = TYPE_TO_SLUG[type];
    if (!slug) {
      toast.error(`Type de document inconnu : ${type}`);
      return;
    }
    try {
      const res = await fetch(`${API}/api/metier/tef/dossiers/${dossierId}/${slug}/download`, { headers, credentials: "include" });
      if (res.ok) {
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a"); a.href = url; a.download = `${slug}-${dossierId}.pdf`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || `Erreur ${res.status} lors du téléchargement`);
      }
    } catch (e) {
      console.error(e);
      toast.error(`Erreur réseau : ${e.message}`);
    }
  };

  // Voir = ouvrir le PDF dans un nouvel onglet (mockup : Voir + Télécharger).
  // Fix pop-up blocker : openAuthedPdf ouvre l'onglet DANS le geste utilisateur
  // (avant l'await), sinon le window.open post-fetch est bloqué.
  const handleView = async (type) => {
    const token = localStorage.getItem("session_token");
    const headers = { "X-Tenant-ID": tenantId || "tef", ...(token && { Authorization: `Bearer ${token}` }) };
    const slug = TYPE_TO_SLUG[type];
    if (!slug) { toast.error(`Type de document inconnu : ${type}`); return; }
    await openAuthedPdf({
      url: `${API}/api/metier/tef/dossiers/${dossierId}/${slug}/download`,
      headers,
      onError: (msg) => toast.error(msg || "Erreur à l'ouverture"),
    });
  };

  const LABELS = { devis_amo: "Devis AMO", contrat_amo: "Contrat AMO", mandat_amo: "Mandat ANAH", demande_avance_anah: "Demande d'avance ANAH", facture_amo: "Facture AMO", plan_financement: "Plan de financement", procuration_seing_prive: "Procuration (Cerfa 13463)" };

  // LOT 1 — partition par zone : "amo" = documents AMO uniquement, "anah" = tout
  // le reste (pièces officielles ANAH). amo ∪ anah = tous les docs → zéro perte.
  const visibleDocs = docs.filter((doc) => {
    const isAmo = AMO_TYPES.includes(doc.type_document);
    if (scope === "amo") return isAmo;
    if (scope === "anah") return !isAmo;
    return true;
  });

  if (loading) return <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin" style={{ color: "var(--oh-text-muted)" }} /></div>;

  if (visibleDocs.length === 0) {
    const emptyLabel = scope === "amo"
      ? "Aucun document AMO généré"
      : scope === "anah"
        ? "Aucune pièce ANAH générée"
        : "Aucun document généré";
    return (
      <div className="rounded-xl p-8 text-center" style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}>
        <FileText className="w-10 h-10 mx-auto mb-3 opacity-40" style={{ color: "var(--oh-text-muted)" }} />
        <p className="text-sm" style={{ color: "var(--oh-text-muted)" }}>{emptyLabel}</p>
      </div>
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid={`generated-docs-grid${scope !== "all" ? "-" + scope : ""}`}>
      {visibleDocs.map((doc) => (
        <div
          key={doc.type_document}
          className="rounded-xl p-5 transition-all"
          style={{ background: "var(--oh-bg-elevated)", border: "1px solid var(--oh-border)" }}
        >
          <div className="flex items-center gap-3 mb-3">
            <div className="p-2 rounded-lg" style={{ background: "rgba(239,68,68,0.15)" }}>
              <FileText className="w-5 h-5 text-[var(--oh-error-text)]" />
            </div>
            <div>
              <p className="text-sm font-medium" style={{ color: "var(--oh-text)" }}>{LABELS[doc.type_document] || doc.type_document}</p>
              <p className="text-xs" style={{ color: "var(--oh-text-muted)" }}>
                Statut : {doc.statut || "généré"} | {formatDate(doc.created_at)}
              </p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              size="sm" variant="outline"
              className="flex-1"
              onClick={() => handleView(doc.type_document)}
              data-testid={`view-${doc.type_document}`}
            >
              <Eye className="w-4 h-4 mr-2" />
              Voir
            </Button>
            <Button
              size="sm"
              className="flex-1 bg-[var(--oh-accent-solid)] hover:bg-[var(--oh-accent-solid)] text-[var(--oh-accent-contrast)]"
              onClick={() => handleDownload(doc.type_document)}
              data-testid={`download-${doc.type_document}`}
            >
              <Download className="w-4 h-4 mr-2" />
              Télécharger
            </Button>
          </div>
        </div>
      ))}
    </div>
  );
}

export function DocThumbnailCard({ doc, nom, ext, isImage, isPdf, previewUrl, tenantId, onDownload }) {
  const [imgSrc, setImgSrc] = useState(null);
  const [imgError, setImgError] = useState(false);

  // Charger l'aperçu via fetch + blob (images ET PDFs via /thumbnail)
  useEffect(() => {
    if (!previewUrl) return;
    let cancelled = false;
    const loadPreview = async () => {
      try {
        const token = localStorage.getItem('session_token');
        const res = await fetch(previewUrl, {
          headers: { "X-Tenant-ID": tenantId || "tef", ...(token && { "Authorization": `Bearer ${token}` }) },
          credentials: 'include'
        });
        if (res.ok && !cancelled) {
          const blob = await res.blob();
          if (blob.size > 0) {
            setImgSrc(URL.createObjectURL(blob));
          } else {
            setImgError(true);
          }
        } else {
          if (!cancelled) setImgError(true);
        }
      } catch {
        if (!cancelled) setImgError(true);
      }
    };
    loadPreview();
    return () => { cancelled = true; };
  }, [previewUrl, tenantId]);

  // Cleanup blob URL on unmount
  useEffect(() => {
    return () => { if (imgSrc) URL.revokeObjectURL(imgSrc); };
  }, [imgSrc]);

  return (
    <button
      onClick={onDownload}
      className="group rounded-lg overflow-hidden text-left transition-all hover:scale-[1.02]"
      style={{ background: "var(--oh-bg-surface)", border: "1px solid var(--oh-border)" }}
      data-testid={`doc-thumbnail-${doc.id}`}
    >
      {/* Thumbnail area — compact square */}
      <div className="aspect-square flex items-center justify-center relative" style={{ background: isImage ? "var(--oh-bg)" : isPdf ? "rgba(239,68,68,0.08)" : "rgba(59,130,246,0.08)" }}>
        {imgSrc && !imgError ? (
          <img src={imgSrc} alt={nom} className="w-full h-full object-cover" />
        ) : previewUrl && !imgError ? (
          <Loader2 className="w-4 h-4 animate-spin" style={{ color: "var(--oh-text-muted)" }} />
        ) : isPdf ? (
          <div className="flex flex-col items-center">
            <FileText className="w-6 h-6 text-[var(--oh-error-text)] opacity-70" />
            <span className="text-[8px] font-medium text-[var(--oh-error-text)] mt-0.5">PDF</span>
          </div>
        ) : (
          <div className="flex flex-col items-center">
            <FileText className="w-6 h-6 text-[var(--oh-accent-text)] opacity-70" />
            <span className="text-[8px] font-medium mt-0.5" style={{ color: "var(--oh-text-muted)" }}>.{ext}</span>
          </div>
        )}
        <div className="absolute inset-0 bg-[var(--oh-bg)] group-hover:bg-[var(--oh-bg)] transition-all flex items-center justify-center">
          <Download className="w-4 h-4 text-[var(--oh-text)] opacity-0 group-hover:opacity-100 transition-all" />
        </div>
      </div>
      {/* File name — compact */}
      <div className="px-1.5 py-1.5">
        <p className="text-[10px] truncate font-medium" style={{ color: "var(--oh-text)" }}>
          {nom}
        </p>
      </div>
    </button>
  );
}
